/**
 * Lista de propostas do consultor (com filtros) — `/crm/propostas`.
 * Backbone P0.2 — funcional para gerir rascunhos.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Plus, FileText, MagnifyingGlass, Trash, Eye } from "@phosphor-icons/react";

const fmt = (v) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v || 0);

const ESTADO_LABEL = {
  rascunho: { label: "Rascunho", cls: "bg-gray-100 text-gray-700" },
  enviada: { label: "Enviada", cls: "bg-blue-100 text-blue-800" },
  vista: { label: "Vista", cls: "bg-amber-100 text-amber-800" },
  aceite: { label: "Aceite", cls: "bg-emerald-100 text-emerald-800" },
  recusada: { label: "Recusada", cls: "bg-red-100 text-red-800" },
  expirada: { label: "Expirada", cls: "bg-zinc-200 text-zinc-700" },
};

export default function CRMPropostasListPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState("");
  const [filter, setFilter] = useState("");

  const load = () => {
    setLoading(true);
    const qs = estado ? `?estado=${encodeURIComponent(estado)}` : "";
    api.get(`/propostas${qs}`)
      .then((r) => setItems(r?.data?.items || []))
      .finally(() => setLoading(false));
  };

  useEffect(load, [estado]);

  const filtered = items.filter((it) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      (it.NomeClienteEmpresa || "").toLowerCase().includes(q) ||
      (it.Referencia || "").toLowerCase().includes(q) ||
      (it.TituloPrograma || "").toLowerCase().includes(q)
    );
  });

  const remove = async (id) => {
    if (!window.confirm("Apagar este rascunho?")) return;
    try {
      await api.delete(`/propostas/${id}`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8 px-4 sm:px-6 lg:px-8" data-testid="propostas-list">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-black tracking-tighter text-[#0F1829] flex items-center gap-3" style={{ fontFamily: "'Fraunces', serif" }}>
              <FileText size={28} weight="duotone" className="text-[#002480]" />
              Propostas Comerciais
            </h1>
            <p className="text-sm text-[#6B7280] mt-1">Gere as suas propostas B2B aqui.</p>
          </div>
          <button onClick={() => navigate("/propostas/nova")} data-testid="propostas-new-btn"
            className="px-4 py-2 bg-[#0F1829] text-white text-sm font-semibold rounded-md hover:bg-[#001f6e] inline-flex items-center gap-2">
            <Plus size={16} weight="bold" /> Nova Proposta
          </button>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 mb-4 flex items-center gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input type="text" placeholder="Procurar por nome, referência ou título..."
              value={filter} onChange={(e) => setFilter(e.target.value)}
              data-testid="propostas-search"
              className="w-full pl-9 pr-3 py-2 border border-[#E5E7EB] rounded-md text-sm" />
          </div>
          <select value={estado} onChange={(e) => setEstado(e.target.value)}
            data-testid="propostas-filter-estado"
            className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm">
            <option value="">Todos os estados</option>
            <option value="rascunho">Rascunho</option>
            <option value="enviada">Enviada</option>
            <option value="vista">Vista</option>
            <option value="aceite">Aceite</option>
            <option value="recusada">Recusada</option>
            <option value="expirada">Expirada</option>
          </select>
        </div>

        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] text-left text-xs uppercase tracking-wide text-[#6B7280]">
              <tr>
                <th className="px-4 py-3">Referência</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Título</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Validade</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#9CA3AF]">A carregar...</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#9CA3AF]" data-testid="propostas-empty">
                  Sem propostas. <button onClick={() => navigate("/propostas/nova")} className="text-[#0F1829] font-medium underline">Criar a primeira</button>.
                </td></tr>
              )}
              {filtered.map((it) => {
                const e = ESTADO_LABEL[it.Estado] || ESTADO_LABEL.rascunho;
                return (
                  <tr key={it.ID_Proposta} className="border-t border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`proposta-row-${it.ID_Proposta}`}>
                    <td className="px-4 py-3 font-mono text-xs">{it.Referencia}</td>
                    <td className="px-4 py-3 font-medium">{it.NomeClienteEmpresa || "—"}</td>
                    <td className="px-4 py-3 text-[#6B7280] text-xs">{it.TituloPrograma || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${e.cls}`}>{e.label}</span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt(it.TotalFinal)}</td>
                    <td className="px-4 py-3 text-xs text-[#6B7280]">{it.ValidaAte || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => navigate(`/propostas/${it.ID_Proposta}`)}
                          data-testid={`proposta-edit-${it.ID_Proposta}`}
                          className="p-1.5 text-[#0F1829] hover:bg-[#F3F4F6] rounded" title="Editar"><Eye size={16} /></button>
                        {it.Estado === "rascunho" && (
                          <button onClick={() => remove(it.ID_Proposta)}
                            data-testid={`proposta-delete-${it.ID_Proposta}`}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Apagar rascunho"><Trash size={16} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
